import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import AgeGate from "@/components/AgeGate";
import Layout from "@/components/Layout";
import PwaControls from "@/components/PwaControls";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Cart from "@/pages/Cart";
import MyOrders from "@/pages/MyOrders";
import { CartProvider } from "@/cart/CartContext";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import Auth from "@/pages/Auth";
import { Loader2 } from "lucide-react";

// Code-split secondary routes — each loads its chunk on first visit.
const Quality = lazy(() => import("@/pages/Quality"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Admin = lazy(() => import("@/pages/Admin"));
const PaymentComplete = lazy(() => import("@/pages/PaymentComplete"));
const Vendors = lazy(() => import("@/pages/Vendors"));
const VendorPortal = lazy(() => import("@/pages/VendorPortal"));
const Library = lazy(() => import("@/pages/Library"));
const Resources = lazy(() => import("@/pages/Resources"));
const Discussions = lazy(() => import("@/pages/Discussions"));
const DiscussionThread = lazy(() => import("@/pages/DiscussionThread"));
const Membership = lazy(() => import("@/pages/Membership"));
const Track = lazy(() => import("@/pages/Track"));
const Terms = lazy(() => import("@/pages/Terms"));
const Support = lazy(() => import("@/pages/Support"));
const CryptoBriefing = lazy(() => import("@/pages/CryptoBriefing"));
const VendorDemo = lazy(() => import("@/pages/VendorDemo"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Affiliate = lazy(() => import("@/pages/Affiliate"));
const AffiliateBank = lazy(() => import("@/pages/AffiliateBank"));

let lenisInstance = null;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  // Clicking a link that points at the current page scrolls back to the top
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//") || href.includes("#")) return;
      if (href === window.location.pathname) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (lenisInstance) lenisInstance.scrollTo(0);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return null;
}

function TrackPageViews() {
  const { pathname } = useLocation();
  const { token } = useAuth();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/track`,
      { kind: "page_view", path: pathname },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    ).catch(() => {});
  }, [pathname, token]);
  return null;
}

function ConditionalAgeGate({ verified, onAccept }) {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <AgeGate verified={verified} onAccept={onAccept} />;
}

function SiteGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (["/admin", "/reset-password", "/affiliate", "/track", "/terms", "/privacy", "/crypto-briefing", "/vendor-demo"].includes(location.pathname)) return children;
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-clinical" />
      </div>
    );
  }
  if (!user) return <Auth />;
  return children;
}

function App() {
  const [verified, setVerified] = useState(
    () => sessionStorage.getItem("vf-age-verified") === "true"
  );

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    lenisInstance = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenisInstance = null;
      lenis.destroy();
    };
  }, []);

  const accept = () => {
    sessionStorage.setItem("vf-age-verified", "true");
    setVerified(true);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ConditionalAgeGate verified={verified} onAccept={accept} />
        <PwaControls />
        <AuthProvider>
          <CartProvider>
            <TrackPageViews />
            <Layout>
            <SiteGate>
              <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading"><Loader2 className="h-6 w-6 animate-spin text-clinical" /></div>}>
              <Routes>
                <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/quality" element={<Quality />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/cart" element={<Cart />} />
                <Route path="/auth" element={<Home />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/orders" element={<MyOrders />} />
                <Route path="/my-orders" element={<MyOrders />} />
                <Route path="/payment/complete" element={<PaymentComplete />} />
                <Route path="/vendors" element={<Vendors />} />
                <Route path="/marketplace" element={<Vendors />} />
                <Route path="/vendors/:vendorId" element={<Vendors />} />
                <Route path="/vendor" element={<VendorPortal />} />
                <Route path="/library" element={<Library />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/discussions" element={<Discussions />} />
                <Route path="/the-wardroom" element={<Discussions />} />
                <Route path="/discussions/:threadId" element={<DiscussionThread />} />
                <Route path="/membership" element={<Membership />} />
                <Route path="/track" element={<Track />} />
                <Route path="/support" element={<Support />} />
                <Route path="/crypto-briefing" element={<CryptoBriefing />} />
                <Route path="/vendor-demo" element={<VendorDemo />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/affiliate" element={<Affiliate />} />
                <Route path="/affiliate/bank" element={<AffiliateBank />} />
              </Routes>
              </Suspense>
            </SiteGate>
            </Layout>
          </CartProvider>
        </AuthProvider>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
